/**
 * Canvas-based screenshot capture with PII redaction
 */

import { ScreenshotOptions } from './types';
import { logger as defaultLogger, Logger } from './logger';

export class CanvasScreenshotCapture {
  private readonly logger: Logger;
  private piiPatterns: Record<string, RegExp>;

  constructor(customPatterns?: Record<string, RegExp>, logger: Logger = defaultLogger) {
    this.logger = logger;
    // Default PII patterns — no global flag since we only need boolean detection
    // Using the `g` flag with RegExp.test() mutates lastIndex and yields
    // inconsistent results across repeated calls.
    this.piiPatterns = {
      email: /[\w.-]+@[\w.-]+\.\w+/,
      phone: /[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}/,
      ssn: /\d{3}-\d{2}-\d{4}/,
      creditCard: /\d{4}[\s.-]?\d{4}[\s.-]?\d{4}[\s.-]?\d{4}/,
      ...customPatterns
    };
  }

  /**
   * Safely export a canvas to a data URL.
   * canvas.toDataURL can throw a SecurityError on tainted canvases (e.g. when
   * cross-origin images were drawn). This helper catches that and falls back to
   * canvas.toBlob → FileReader → base64 data URL. If both paths fail the
   * returned promise rejects with an explanatory error.
   */
  private safeCanvasToDataURL(
    canvas: HTMLCanvasElement,
    format: string,
    quality: number
  ): Promise<string> {
    const mimeType = `image/${format}`;

    // Fast path — try synchronous toDataURL first
    try {
      return Promise.resolve(canvas.toDataURL(mimeType, quality));
    } catch (toDataURLError) {
      this.logger.warn('[ScreenCapture] toDataURL failed (tainted canvas?), trying toBlob fallback:', toDataURLError);
    }

    // Fallback — asynchronous toBlob → FileReader
    return new Promise<string>((resolve, reject) => {
      try {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('[ScreenCapture] canvas.toBlob returned null — canvas may be tainted'));
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
              if (typeof reader.result === 'string') {
                resolve(reader.result);
              } else {
                reject(new Error('[ScreenCapture] FileReader did not produce a string result'));
              }
            };
            reader.onerror = () => {
              reject(new Error('[ScreenCapture] FileReader failed to read blob'));
            };
            reader.readAsDataURL(blob);
          },
          mimeType,
          quality
        );
      } catch (toBlobError) {
        reject(
          new Error(
            `[ScreenCapture] Both toDataURL and toBlob failed for canvas export. ` +
            `toBlob error: ${toBlobError instanceof Error ? toBlobError.message : String(toBlobError)}`
          )
        );
      }
    });
  }

  /**
   * Strip <script> elements from a Document to prevent script re-execution
   * when writing HTML into a same-origin iframe clone.
   * Also removes inline event-handler attributes (onclick, onerror, onload, etc.)
   * to further prevent code execution in the cloned DOM.
   */
  private static sanitizeClonedDocument(doc: Document): void {
    CanvasScreenshotCapture.stripExecutable(doc);
  }

  /**
   * Everything that could execute or load, removed from a document or fragment.
   *
   * `iframe, frame, object, embed` matter as much as `script` here. They are NESTED
   * BROWSING CONTEXTS: copied into the clone they LOAD, fetching the ad / chat widget
   * / embed again and running its scripts, on every screenshot. The sandbox used to
   * mask this — sandbox flags propagate to nested contexts — so removing the sandbox
   * without removing these would have traded three console lines for third-party code
   * execution. They cost nothing visually: the capture paints via SVG foreignObject,
   * which cannot render a nested browsing context, so they were always blank.
   *
   * `noscript` is stripped even though the clone is no longer scripting-disabled: it
   * keeps "please enable JavaScript" banners out of the image, and keeps this correct
   * if anything writes the markup into a scripting-disabled document again — such a
   * document parses noscript CONTENTS as live DOM, turning tracking pixels into real
   * <img> elements that fire per capture.
   */
  private static stripExecutable(root: Document | DocumentFragment): void {
    root
      .querySelectorAll('script, noscript, iframe, frame, object, embed')
      .forEach((el) => { el.remove(); });

    // <meta http-equiv="refresh"> NAVIGATES the clone. Verified in Chrome: with the
    // sandbox the frame stayed put, without it the same markup took the frame to the
    // refresh target — so the capture would photograph a different document, and the
    // page would be fetched again on every screenshot. The sandbox was suppressing a
    // whole class of "automatic features", not just scripts; this and the nested
    // contexts above are the two members of that class that actually reach us.
    // (Checked and NOT an issue: images and stylesheets load either way and the
    // screenshot needs them, autofocus does not steal host focus, audio stays paused.)
    //
    // Matched on the attribute VALUE rather than a selector so casing and stray
    // whitespace cannot slip a refresh through.
    root.querySelectorAll('meta[http-equiv]').forEach((el) => {
      const kind = (el.getAttribute('http-equiv') || '').trim().toLowerCase();
      if (kind === 'refresh') el.remove();
    });

    const handlerPattern = /^on/i;
    root.querySelectorAll('*').forEach((el) => {
      for (const attr of Array.from(el.attributes)) {
        if (handlerPattern.test(attr.name)) {
          el.removeAttribute(attr.name);
        }
      }
    });

    // querySelectorAll does NOT descend into <template>.content — it is a separate
    // DocumentFragment — so everything above misses it and the content is serialised
    // straight back out. Verified in a browser: a script inside a template survived a
    // full sanitise/serialise round trip. Recursing also covers nested templates.
    root.querySelectorAll('template').forEach((tpl) => {
      CanvasScreenshotCapture.stripExecutable(tpl.content);
    });
  }

  /**
   * Get all text nodes in the document
   */
  private getAllTextNodes(): { node: Node; rect: DOMRect }[] {
    const textNodes: { node: Node; rect: DOMRect }[] = [];
    if (!document.body) return textNodes;
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;

          // Skip invisible elements
          const style = window.getComputedStyle(parent);
          if (style.display === 'none' || style.visibility === 'hidden') {
            return NodeFilter.FILTER_REJECT;
          }

          // Skip empty text
          if (!node.textContent || !node.textContent.trim()) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    let node;
    while (node = walker.nextNode()) {
      const range = document.createRange();
      range.selectNode(node);
      const rect = range.getBoundingClientRect();

      if (rect.width > 0 && rect.height > 0) {
        textNodes.push({ node, rect });
      }
    }

    return textNodes;
  }

  /**
   * Check if text contains PII
   */
  private containsPII(text: string): { contains: boolean; type?: string } {
    for (const [type, pattern] of Object.entries(this.piiPatterns)) {
      // Reset lastIndex in case a custom pattern uses the global flag,
      // which would otherwise cause RegExp.test() to yield inconsistent results.
      pattern.lastIndex = 0;
      if (pattern.test(text)) {
        return { contains: true, type };
      }
    }
    return { contains: false };
  }

  /**
   * Apply blur effect to a region on canvas
   */
  private applyBlur(ctx: CanvasRenderingContext2D, region: { x: number; y: number; width: number; height: number }) {
    // Get image data for the region
    const imageData = ctx.getImageData(region.x, region.y, region.width, region.height);
    const data = imageData.data;

    // Simple box blur
    const blurRadius = 5;
    for (let y = 0; y < region.height; y++) {
      for (let x = 0; x < region.width; x++) {
        let r = 0, g = 0, b = 0, a = 0;
        let count = 0;

        for (let dy = -blurRadius; dy <= blurRadius; dy++) {
          for (let dx = -blurRadius; dx <= blurRadius; dx++) {
            const nx = x + dx;
            const ny = y + dy;

            if (nx >= 0 && nx < region.width && ny >= 0 && ny < region.height) {
              const idx = (ny * region.width + nx) * 4;
              r += data[idx];
              g += data[idx + 1];
              b += data[idx + 2];
              a += data[idx + 3];
              count++;
            }
          }
        }

        const idx = (y * region.width + x) * 4;
        data[idx] = r / count;
        data[idx + 1] = g / count;
        data[idx + 2] = b / count;
        data[idx + 3] = a / count;
      }
    }

    ctx.putImageData(imageData, region.x, region.y);
  }

  /**
   * Capture screenshot with redaction
   */
  async captureWithRedaction(options: ScreenshotOptions = {}): Promise<string> {
    const {
      redact = true,
      scale = 0.6,
      quality = 0.7,
      format = 'webp'
    } = options;

    // Ensure document.body exists
    if (!document.body) {
      this.logger.warn('[ScreenCapture] Document body not available');
      return '';
    }

    // Step 1: Identify PII regions if redaction is enabled
    const piiRegions: Array<{ x: number; y: number; width: number; height: number; type: string }> = [];

    if (redact) {
      const textNodes = this.getAllTextNodes();

      for (const { node, rect } of textNodes) {
        const piiCheck = this.containsPII(node.textContent || '');
        if (piiCheck.contains) {
          piiRegions.push({
            x: rect.left,
            y: rect.top,
            width: rect.width,
            height: rect.height,
            type: piiCheck.type || 'unknown'
          });
        }
      }
    }

    // Step 2: Create canvas and draw the page
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      this.logger.error('[ScreenCapture] Failed to get 2D canvas context');
      return Promise.reject(new Error('[ScreenCapture] Canvas 2D context unavailable'));
    }

    // Set canvas size
    const width = window.innerWidth * scale;
    const height = window.innerHeight * scale;
    canvas.width = width;
    canvas.height = height;

    return new Promise<string>((resolve, reject) => {
      // A temporary same-origin iframe holding a copy of the page to photograph.
      // contentDocument must stay reachable — the PII redaction below edits it.
      //
      // NOT sandboxed, deliberately. `sandbox="allow-same-origin"` was added here as
      // belt-and-braces over the sanitisation below, and it cost more than it gave:
      //
      //   - Chrome logs "Blocked script execution … the document's frame is
      //     sandboxed" THREE times per capture, into the HOST PAGE's console. We are
      //     injected into customers' sites; filling their console with red errors is
      //     the first thing their engineer sees when they suspect us.
      //   - It bought nothing. Writing the sanitised HTML into a NON-sandboxed clone
      //     runs no scripts at all — verified in a browser against inline scripts,
      //     head scripts and scripts inside <template>. The sanitisation below is
      //     what actually stops execution; the sandbox was only ever blocking things
      //     that had already been removed.
      //   - It actively caused a bug. A scripting-DISABLED document parses <noscript>
      //     CONTENTS as live DOM, so tracking pixels inside noscript became real
      //     <img> elements and fired on every screenshot. No sandbox, no such
      //     parsing — the hazard disappears with the attribute.
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.top = '0';
      iframe.style.left = '0';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = 'none';
      iframe.style.zIndex = '-999999';

      // Re-check document.body — it can become null during page navigation
      if (!document.body) {
        this.logger.warn('[ScreenCapture] Document body became null before iframe append');
        this.safeCanvasToDataURL(canvas, format, quality).then(resolve, reject);
        return;
      }
      document.body.appendChild(iframe);

      // Clone the document into the iframe.
      //
      // Sanitise BEFORE writing, not after. `document.write` parses AND EXECUTES
      // synchronously, so stripping scripts from the resulting DOM was always too
      // late — the host page's scripts had already run inside this hidden iframe on
      // every single screenshot. On LeadRat that meant its frame-buster fired
      // ("Page displayed in frame - execution stopped") and killed the capture; on a
      // page with analytics it means silently double-firing them.
      //
      // DOMParser builds a document WITHOUT executing anything, so the strip happens
      // while the scripts are still inert markup.
      const parsed = new DOMParser().parseFromString(
        document.documentElement.outerHTML,
        'text/html',
      );
      CanvasScreenshotCapture.sanitizeClonedDocument(parsed);

      const clonedDoc = iframe.contentDocument!;
      clonedDoc.open();
      clonedDoc.write(parsed.documentElement.outerHTML);
      clonedDoc.close();

      // Sanitise the written document too. The pre-write pass is what prevents
      // execution; this second pass catches anything the serialise/reparse round
      // trip reintroduced, and is now the only guard, since the sandbox is gone.
      CanvasScreenshotCapture.sanitizeClonedDocument(clonedDoc);

      // Apply redactions in the cloned document
      if (redact && piiRegions.length > 0) {
        const style = clonedDoc.createElement('style');
        style.textContent = `
          .pii-redacted {
            filter: blur(5px);
            background: #000;
            color: transparent !important;
          }
        `;
        clonedDoc.head.appendChild(style);

        // Mark PII elements for redaction - ensure body exists
        if (!clonedDoc.body) {
          this.logger.warn('[ScreenCapture] Cloned document has no body');
          document.body.removeChild(iframe);
          this.safeCanvasToDataURL(canvas, format, quality).then(resolve, reject);
          return;
        }

        const walker = clonedDoc.createTreeWalker(
          clonedDoc.body,
          NodeFilter.SHOW_TEXT
        );

        let node;
        while (node = walker.nextNode()) {
          const piiCheck = this.containsPII(node.textContent || '');
          if (piiCheck.contains && node.parentElement) {
            node.parentElement.classList.add('pii-redacted');
          }
        }
      }

      // Helper to clean up the iframe in all exit paths
      const removeIframe = () => {
        try { document.body.removeChild(iframe); } catch (_) { /* already removed */ }
      };

      // Wait for the cloned document to render, then capture it
      setTimeout(() => {
        try {
          // Render the iframe content into the canvas.
          // The iframe is same-origin (cloned from our document), so we can
          // use an SVG foreignObject serialisation that paints the cloned DOM
          // into an Image we can drawImage.
          const iframeWindow = iframe.contentWindow;

          if (!iframeWindow) {
            this.logger.warn('[ScreenCapture] iframe contentWindow not available');
            removeIframe();
            this.safeCanvasToDataURL(canvas, format, quality).then(resolve, reject);
            return;
          }

          // Build an SVG that wraps the serialised (sanitized) HTML as a
          // foreignObject. This is a widely-supported technique for painting
          // DOM content onto a canvas without requiring html2canvas.
          const serializer = new XMLSerializer();
          const clonedHtml = serializer.serializeToString(clonedDoc.documentElement);
          const svgData = `<svg xmlns="http://www.w3.org/2000/svg" width="${window.innerWidth}" height="${window.innerHeight}">` +
            `<foreignObject width="100%" height="100%">` +
            clonedHtml +
            `</foreignObject></svg>`;
          const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
          const svgUrl = URL.createObjectURL(svgBlob);

          const img = new Image();
          img.onload = () => {
            // Draw the rendered page onto the canvas
            ctx.save();
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);
            ctx.restore();

            URL.revokeObjectURL(svgUrl);

            // Apply blur to PII regions AFTER the page content is rendered
            if (redact && piiRegions.length > 0) {
              for (const region of piiRegions) {
                this.applyBlur(ctx, {
                  x: region.x * scale,
                  y: region.y * scale,
                  width: region.width * scale,
                  height: region.height * scale
                });
              }
            }

            removeIframe();

            // Return base64 encoded image (safe against tainted canvas)
            this.safeCanvasToDataURL(canvas, format, quality).then(resolve, reject);
          };
          img.onerror = () => {
            URL.revokeObjectURL(svgUrl);
            this.logger.warn('[ScreenCapture] SVG image render failed, returning blank canvas');
            removeIframe();
            this.safeCanvasToDataURL(canvas, format, quality).then(resolve, reject);
          };
          img.src = svgUrl;
        } catch (err) {
          this.logger.error('[ScreenCapture] Failed to render iframe content:', err);
          removeIframe();
          this.safeCanvasToDataURL(canvas, format, quality).then(resolve, reject);
        }
      }, 100);
    });
  }

  /**
   * Simple screenshot without redaction (faster)
   */
  async captureSimple(options: ScreenshotOptions = {}): Promise<string> {
    return this.captureWithRedaction({ ...options, redact: false });
  }
}
